import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { PageHeader } from '@/components/common/PageHeader'
import { StatusBadge } from '@/components/common/StatusBadge'
import { EmptyState } from '@/components/common/EmptyState'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type Organization = Tables<'organizations'>
type OrgMetrics = { userCount: number; orderCount: number; revenue: number }

export function AdminOrganizationsPage() {
  const navigate = useNavigate()
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [metrics, setMetrics] = useState<Record<string, OrgMetrics>>({})
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [newOrg, setNewOrg] = useState({ name: '', slug: '', email: '' })

  const load = async () => {
    const [orgRes, orderRes, profileRes] = await Promise.all([
      supabase.from('organizations').select('*').order('name'),
      supabase.from('orders').select('organization_id, total'),
      supabase.from('profiles').select('id, organization_id'),
    ])

    const orgData = orgRes.data || []
    setOrgs(orgData)

    const m: Record<string, OrgMetrics> = {}
    for (const org of orgData) {
      m[org.id] = { userCount: 0, orderCount: 0, revenue: 0 }
    }
    for (const p of profileRes.data || []) {
      if (p.organization_id && m[p.organization_id]) {
        m[p.organization_id].userCount++
      }
    }
    for (const o of orderRes.data || []) {
      if (m[o.organization_id]) {
        m[o.organization_id].orderCount++
        m[o.organization_id].revenue += Number(o.total)
      }
    }
    setMetrics(m)
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const addOrg = async () => {
    await supabase.from('organizations').insert({
      name: newOrg.name,
      slug: newOrg.slug || newOrg.name.toLowerCase().replace(/\s+/g, '-'),
      email: newOrg.email || null,
    })
    setNewOrg({ name: '', slug: '', email: '' })
    setShowAdd(false)
    load()
  }

  return (
    <div>
      <PageHeader title="Organizations" description="Manage distributor organizations" actions={
        <button className={css.btnPrimary} onClick={() => setShowAdd(!showAdd)}>
          Add Organization
        </button>
      } />

      {showAdd && (
        <div className={css.card} style={{ marginBottom: 'var(--space-6)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Name *</label>
              <input className={css.formInput} value={newOrg.name} onChange={e => setNewOrg({ ...newOrg, name: e.target.value })} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Slug</label>
              <input className={css.formInput} value={newOrg.slug} onChange={e => setNewOrg({ ...newOrg, slug: e.target.value })} placeholder="auto-generated" />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Email</label>
              <input type="email" className={css.formInput} value={newOrg.email} onChange={e => setNewOrg({ ...newOrg, email: e.target.value })} />
            </div>
            <button className={css.btnPrimary} onClick={addOrg} disabled={!newOrg.name}>Save</button>
            <button className={css.btnSecondary} onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p> : orgs.length === 0 ? (
        <EmptyState title="No organizations" description="Add your first distributor organization to get started" />
      ) : (
        <div className={css.card} style={{ padding: 0, overflow: 'hidden' }}>
          <table className={css.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Users</th>
                <th>Orders</th>
                <th>Revenue</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map(org => {
                const m = metrics[org.id] || { userCount: 0, orderCount: 0, revenue: 0 }
                return (
                  <tr key={org.id} className={css.tableClickableRow} onClick={() => navigate(`/admin/organizations/${org.id}`)}>
                    <td style={{ fontWeight: 600 }}>{org.name}</td>
                    <td><code style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{org.slug}</code></td>
                    <td>{m.userCount}</td>
                    <td>{m.orderCount}</td>
                    <td style={{ fontWeight: 500 }}>{m.revenue > 0 ? `£${m.revenue.toLocaleString()}` : '—'}</td>
                    <td>
                      <StatusBadge
                        label={org.is_active ? 'active' : 'inactive'}
                        variant={org.is_active ? 'success' : 'default'}
                      />
                    </td>
                    <td style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
                      {new Date(org.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
